import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Upload } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, ProvedorCartoesRecolhidos, Selo, type CorSelo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { CartaoVerticais, CampoVerticais, useVerticaisDaCriacao, verticaisDaCriacao } from '../../componentes/verticais';
import { Vazio } from '../../componentes/estados';
import { BotaoLixeira } from '../../componentes/lixeira';
import { BotaoModeloCsv, DialogoImportacao } from '../../componentes/importacao';
import { Formulario, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { del, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dinheiro, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import type { Projeto } from './tipos';

const STATUS = ['planejado', 'em_andamento', 'pausado', 'concluido', 'cancelado'] as const;
const COR: Record<Projeto['status'], CorSelo> = { planejado: 'azul', em_andamento: 'ciano', pausado: 'laranja', concluido: 'verde', cancelado: 'neutro' };

export function Projetos() {
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const [status, setStatus] = useState('');
  const [aberto, setAberto] = useState<Projeto | 'novo' | null>(null);
  const [importar, setImportar] = useState(false);
  const atual = aberto && aberto !== 'novo' ? aberto : null;
  // Ficha 2026-10-22: as verticais já na criação (null = o escopo de quem cria, como antes).
  const [verticais, setVerticais] = useVerticaisDaCriacao(aberto);
  const [excluindo, setExcluindo] = useState(false);

  // Fase 2 (W14): o projeto excluído vai para a lixeira por 30 dias (Lixeira, na barra da aba, desfaz).
  const excluir = useMutation({
    mutationFn: () => del(`/cadastros/projetos/${atual!.id}`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/cadastros/projetos'] });
      setExcluindo(false);
      setAberto(null);
      toast.sucesso(tn('comum.removido'));
    },
    onError: (e) => (setExcluindo(false), toast.erro(mensagemDeErro(e))),
  });

  const mudar = useMutation({
    mutationFn: (v: { id: string; status: string }) => post(`/cadastros/projetos/${v.id}/status`, { status: v.status }),
    onSuccess: async (_r, v) => {
      await qc.invalidateQueries({ queryKey: ['/cadastros/projetos'] });
      if (atual) setAberto({ ...atual, status: v.status as Projeto['status'] });
      toast.sucesso(tn('comum.salvo'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  const campos: DefCampo[] = [
    // Review r12 visual M3: o projeto que nasce do ganho da oportunidade traz o nome do cliente no nome e na descrição gerada.
    { nome: 'nome', rotulo: t('campos.projeto'), tipo: 'texto', obrigatorio: true, nomes: [atual?.cliente_nome] },
    { nome: 'cliente_id', rotulo: t('campos.clienteProjeto'), tipo: 'busca', largura: 'meia', busca: { caminho: '/cadastros/parceiros', params: { papel: 'cliente', operacao: true }, rotulo: (i) => String(i.nome) } },
    { nome: 'responsavel_id', rotulo: t('campos.responsavel'), tipo: 'busca', largura: 'meia', busca: { caminho: '/membros', rotulo: (i) => String(i.nome) } },
    { nome: 'inicio', rotulo: t('campos.inicio'), tipo: 'data', largura: 'terco' },
    { nome: 'prazo', rotulo: t('campos.prazo'), tipo: 'data', largura: 'terco' },
    { nome: 'orcamento_centavos', rotulo: t('campos.orcamento'), tipo: 'dinheiro', largura: 'terco' },
    { nome: 'descricao', rotulo: t('campos.descricao'), tipo: 'area', nomes: [atual?.cliente_nome] },
  ];

  const colunas: Coluna<Projeto>[] = [
    { chave: 'nome', rotulo: t('campos.projeto'), principal: true, render: (p) => <span className="font-medium">{textoLivre(p.nome, p.cliente_nome)}</span> },
    { chave: 'cliente_nome', rotulo: t('campos.clienteProjeto') },
    { chave: 'responsavel_nome', rotulo: t('campos.responsavel') },
    {
      chave: 'prazo',
      rotulo: t('campos.prazo'),
      mono: true,
      render: (p) => (
        <span className={p.atrasado ? 'text-laranja' : ''}>
          {data(p.prazo)}
          {p.atrasado ? ` (${t('ficha.atrasado')})` : ''}
        </span>
      ),
    },
    { chave: 'orcamento_centavos', rotulo: t('campos.orcamento'), alinhar: 'direita', mono: true, render: (p) => dinheiro(p.orcamento_centavos, p.moeda) },
    { chave: 'status', rotulo: t('campos.status'), render: (p) => <Selo cor={COR[p.status]}>{t(`status.${p.status}`)}</Selo> },
  ];

  return (
    <>
      {s.pode('cadastros.criar') && (
        <AcaoPrimaria>
          {s.pode('cadastros.excluir') && <BotaoLixeira entidades={['cadastros.projeto']} />}
          {/* Fase 2 (W18, K3): importador de projetos (framework do núcleo, atualiza pelo nome). */}
          {s.pode('cadastros.importar') && s.pode('cadastros.editar') && (
            <>
              <BotaoModeloCsv caminho="/cadastros/projetos" />
              <Botao icone={<Upload className="size-4" />} onClick={() => setImportar(true)}>
                {t('classificacao.importarProjetos')}
              </Botao>
            </>
          )}
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoProjeto')}
          </Botao>
        </AcaoPrimaria>
      )}
      <Tabela<Projeto>
        tela="cadastros.projetos"
        caminho="/cadastros/projetos"
        params={{ status: status || undefined }}
        filtros={{ status }}
        aoAplicarFiltros={(f) => setStatus(String(f.status))}
        colunas={colunas}
        entidade="cadastros.projeto"
        aoClicar={(p) => setAberto(p)}
        vazio={<Vazio texto={t('vazio.projetos')} />}
        filtrosExtra={
          <div className="w-full sm:w-56">
            <Selecao aria-label={t('campos.status')} opcoes={STATUS.map((x) => ({ valor: x, rotulo: t(`status.${x}`) }))} vazio={tn('comum.todos')} value={status} onChange={(e) => setStatus(e.target.value)} />
          </div>
        }
      />
      <Drawer
        aberto={!!aberto}
        aoFechar={() => setAberto(null)}
        largura="lg"
        titulo={atual ? textoLivre(atual.nome, atual.cliente_nome) : t('acoes.novoProjeto')}
        rodape={
          <>
            {atual && s.pode('cadastros.excluir') && (
              <Botao variante="perigo" className="mr-auto" onClick={() => setExcluindo(true)}>
                {t('acoes.excluirProjeto')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={() => setAberto(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-projeto">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {atual && s.pode('cadastros.editar') && (
          <div className="mb-5 flex flex-wrap items-center gap-2">
            <span className="text-sm text-tinta-3">{t('campos.status')}</span>
            {STATUS.map((x) => (
              <button
                key={x}
                type="button"
                disabled={x === atual.status || mudar.isPending}
                onClick={() => mudar.mutate({ id: atual.id, status: x })}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${x === atual.status ? 'border-2 border-acento-forte bg-acento-suave text-acento-forte' : 'border-borda text-tinta-2 hover:text-tinta-1'}`}
              >
                {t(`status.${x}`)}
              </button>
            ))}
          </div>
        )}
        <Formulario
          key={atual?.id ?? 'novo'}
          id="form-projeto"
          campos={campos}
          entidade="cadastros.projeto"
          inicial={(atual ?? {}) as Record<string, unknown>}
          rotulosAtuais={atual ? { cliente_id: atual.cliente_nome ?? '', responsavel_id: atual.responsavel_nome ?? '' } : undefined}
          aoEnviar={async (v) => {
            const corpo = {
              nome: v.nome,
              cliente_id: v.cliente_id || null,
              responsavel_id: v.responsavel_id || null,
              inicio: v.inicio || null,
              prazo: v.prazo || null,
              orcamento_centavos: v.orcamento_centavos ?? null,
              descricao: v.descricao || null,
              extras: v.extras ?? {},
            };
            if (atual) await patch(`/cadastros/projetos/${atual.id}`, corpo);
            else await post('/cadastros/projetos', { ...corpo, ...verticaisDaCriacao(verticais) });
            await qc.invalidateQueries({ queryKey: ['/cadastros/projetos'] });
            toast.sucesso(tn('comum.salvo'));
            setAberto(null);
          }}
        >
          {!atual && <CampoVerticais valor={verticais} aoMudar={setVerticais} />}
        </Formulario>
        {atual && (
          <div className="mt-6 border-t border-borda pt-4">
            {/* Item 10 (21/10): cartão recolhido, como na ficha de Pessoas e empresas. */}
            <ProvedorCartoesRecolhidos>
              <CartaoVerticais entidade="cadastros.projeto" registroId={atual.id} podeEditar={s.pode('cadastros.editar')} />
            </ProvedorCartoesRecolhidos>
            <Chatter entidade="cadastros.projeto" registroId={atual.id} nomes={[atual.cliente_nome]} titulo={atual.nome} />
          </div>
        )}
      </Drawer>
      <Confirmar
        aberto={excluindo}
        aoFechar={() => setExcluindo(false)}
        aoConfirmar={() => excluir.mutate()}
        carregando={excluir.isPending}
        perigoso
        titulo={t('excluirProjeto.titulo')}
        texto={t('excluirProjeto.texto')}
      />
      <DialogoImportacao aberto={importar} aoFechar={() => setImportar(false)} caminho="/cadastros/projetos" titulo={t('classificacao.importarProjetos')} aoConcluir={() => void qc.invalidateQueries({ queryKey: ['/cadastros/projetos'] })} />
    </>
  );
}
